import React from 'react';

import { css, keyframes } from '@emotion/react';
import { NeutralColorToken, Type } from '@robinhood/design';
import { MoneyDollarGranularityMicro } from '@x1/util-format';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';

import LoadingPage from './LoadingPage';
import { CreditLimitRevealPageQuery as CreditLimitRevealPageQueryType } from 'src/clients/Network/gql/build/CreditLimitRevealPageQuery.graphql';
import Button from 'src/components/creditApplication/Button';
import GradientBackgroundLayout from 'src/components/creditApplication/GradientBackgroundLayout';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { useGlobalStore } from 'src/state/global';

const CreditLimitRevealPageQuery = graphql`
  query CreditLimitRevealPageQuery {
    authIdentity {
      id
      creditApplications {
        id
        selectedTerms {
          limitMaxMicro
        }
      }
    }
  }
`;

const CreditLimitRevealPage = () => {
  const { nextPage, nextPageLoading } = React.useContext(NavigationContext);
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const {
    authIdentity: { creditApplications },
  } = useLazyLoadQuery<CreditLimitRevealPageQueryType>(CreditLimitRevealPageQuery, {}, { fetchPolicy: 'network-only' });
  const creditLimitMicro = creditApplications.find((ca) => ca.id === creditApplicationId)?.selectedTerms?.limitMaxMicro;

  const onContinue = React.useCallback(() => {
    nextPage();
  }, [nextPage]);

  return (
    <GradientBackgroundLayout>
      <div css={[root, screenFadeIn]}>
        <div css={textContainer}>
          <Type.Text size='M' color={NeutralColorToken.Fg2}>
            Your credit limit
          </Type.Text>
          <div css={creditLimitContainer}>
            <Type.Text size='L' overrideRootStyles={creditLimitText}>
              {MoneyDollarGranularityMicro(creditLimitMicro ?? 0)}
            </Type.Text>
          </div>
        </div>
        <div css={footer}>
          <Type.Text size='S' color={NeutralColorToken.Fg2}>
            You’ll be evaluated for a limit increase automatically over time.
          </Type.Text>
          <Button
            label='Continue'
            state={nextPageLoading ? 'loading' : 'default'}
            width='full'
            type='submit'
            onClick={onContinue}
          />
        </div>
      </div>
    </GradientBackgroundLayout>
  );
};

const root = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 32px;
  margin-top: 50px;
`;

const textContainer = css`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const creditLimitContainer = css`
  position: relative;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const screenFadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const gradientReveal = keyframes`
  0% {
    background-position: -100% 0%;
    opacity: 0.0;
  }
  100% {
    background-position: -50% 0%;
    opacity: 1;
  }
`;

const screenFadeIn = css`
  opacity: 0;
  animation: ${screenFadeInAnimation} 1s ease-out forwards;
`;

const creditLimitText = css`
  font-size: 72px;
  letter-spacing: -4%;
  background: linear-gradient(
    135deg,
    #1c180d00 9.29%,
    #1c180d00 18.03%,
    #201c1620 26.5%,
    #32281340 31%,
    #33291260 33%,
    #1c180d 35.4%,
    #26211a 44.14%,
    #7d672a 52.77%,
    #574617 60.4%,
    #a18433 68.48%,
    #af9346 76.55%,
    #c8af6c 85.29%,
    #c1a046 93.7%
  );
  background-size: 300% 100%;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 0;
  animation: ${gradientReveal} 4.5s ease-in-out 0.8s forwards;
`;

const footer = css`
  display: flex;
  flex-direction: column;
  text-align: center;
  width: 100%;
  gap: 24px;
`;

const WithSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <CreditLimitRevealPage />
    </React.Suspense>
  );
};
export default WithSuspense;
